import React from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  RuntimeEmptyState,
  RuntimeErrorState,
  RuntimeLoadingState,
} from '../common/runtime';
import { buildApiErrorMessage } from '../../utils/apiError';
import { platformSaasBillingOperationsService } from '../../services/platformSaasBillingOperationsService';

const { Title, Text } = Typography;

const PLACEHOLDER_TAB_ITEMS = [];

const CASE_KEY_FIELDS = [
  'payment_intent_id',
  'request_id',
  'billing_correlation_id',
  'provider_charge_ref',
  'external_charge_ref',
  'charge_id',
  'invoice_id',
  'correlation_id',
  'tenant_id',
];

const TIMELINE_CATEGORY_OPTIONS = [
  { label: 'webhook', value: 'webhook' },
  { label: 'runtime', value: 'runtime' },
  { label: 'transition', value: 'transition' },
  { label: 'operational', value: 'operational' },
  { label: 'payment_intent', value: 'payment_intent' },
  { label: 'charge', value: 'charge' },
  { label: 'invoice', value: 'invoice' },
];

const TIMELINE_ALLOWED_CATEGORIES = new Set(
  TIMELINE_CATEGORY_OPTIONS.map((item) => item.value)
);

const WEBHOOK_PROCESSING_STATUS_OPTIONS = [
  { label: 'received', value: 'received' },
  { label: 'processed', value: 'processed' },
  { label: 'duplicate', value: 'duplicate' },
  { label: 'failed', value: 'failed' },
  { label: 'ignored', value: 'ignored' },
  { label: 'unknown', value: 'unknown' },
];

const WEBHOOK_ALLOWED_PROCESSING_STATUS = new Set(
  WEBHOOK_PROCESSING_STATUS_OPTIONS.map((item) => item.value)
);

const TIMELINE_REFS_FIELDS = [
  'tenant_id',
  'subscription_id',
  'payment_intent_id',
  'charge_id',
  'invoice_id',
  'request_id',
  'correlation_id',
  'billing_correlation_id',
  'provider_key',
  'provider_charge_ref',
];

const PAYLOAD_SANITIZED_FIELDS = [
  'provider_event_id',
  'provider_charge_ref',
  'external_charge_ref',
  'payment_intent_id',
  'charge_id',
  'invoice_id',
  'request_id',
  'correlation_id',
  'billing_correlation_id',
  'event_type',
  'status',
  'description',
  'amount',
  'value',
  'due_date',
  'paid_at',
];

const RESOLVE_CANONICAL_FIELDS = [
  { key: 'tenant_id', label: 'tenant_id' },
  { key: 'subscription_id', label: 'subscription_id' },
  { key: 'invoice_id', label: 'invoice_id' },
  { key: 'charge_id', label: 'charge_id' },
  { key: 'payment_intent_id', label: 'payment_intent_id' },
  { key: 'request_id', label: 'request_id' },
  { key: 'correlation_id', label: 'correlation_id' },
  { key: 'billing_correlation_id', label: 'billing_correlation_id' },
  { key: 'provider_key', label: 'provider_key' },
  { key: 'provider_charge_ref', label: 'provider_charge_ref' },
  { key: 'external_charge_ref', label: 'external_charge_ref' },
  { key: 'webhook_status', label: 'webhook_status' },
  { key: 'activation_outcome', label: 'activation_outcome' },
  { key: 'financial_failure', label: 'financial_failure' },
  { key: 'failure_reason', label: 'failure_reason' },
  { key: 'replay_or_noop', label: 'replay_or_noop' },
  { key: 'noop_reason', label: 'noop_reason' },
  { key: 'current_subscription_status', label: 'current_subscription_status' },
  { key: 'payment_state', label: 'payment_state' },
  { key: 'first_seen_at', label: 'first_seen_at' },
  { key: 'last_seen_at', label: 'last_seen_at' },
];

const normalizeStringValue = (value) => String(value || '').trim();

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('pt-BR');
};

const formatDateCell = (value) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const rawValue = String(value);
    return (
      <Tooltip title={rawValue}>
        <Text type="secondary" style={{ maxWidth: 220 }} ellipsis>
          {`invalida: ${rawValue}`}
        </Text>
      </Tooltip>
    );
  }

  const formatted = parsed.toLocaleString('pt-BR');
  return (
    <Tooltip title={`raw: ${String(value)}`}>
      <Text>{formatted}</Text>
    </Tooltip>
  );
};

const renderCopyableValue = (value, options = {}) => {
  const { maxWidth = 240, copyable = true } = options;
  if (isEmptyValue(value)) {
    return '-';
  }

  const rawValue = String(value);
  return (
    <Text
      style={{ maxWidth, display: 'inline-block' }}
      ellipsis={{ tooltip: rawValue }}
      copyable={copyable ? { text: rawValue } : false}
    >
      {rawValue}
    </Text>
  );
};

const buildOperationalErrorMessage = (requestError, fallbackMessage) => {
  const status = Number(requestError?.response?.status);

  if (status === 400) {
    return 'Solicitacao invalida. Revise os filtros informados e tente novamente.';
  }
  if (status === 401 || status === 403) {
    return 'Acesso nao permitido para esta operacao.';
  }
  if (status === 404) {
    return 'Recurso nao encontrado para os filtros informados.';
  }
  if (status >= 500) {
    return 'Servico temporariamente indisponivel. Tente novamente em instantes.';
  }

  const parsed = buildApiErrorMessage(requestError, fallbackMessage);
  const candidateMessage = normalizeStringValue(parsed?.message);
  const hasTechnicalTrace = /traceback|stack|exception|\sat\s/i.test(candidateMessage);

  if (candidateMessage && !hasTechnicalTrace && candidateMessage.length <= 180) {
    return candidateMessage;
  }

  return fallbackMessage;
};

const STATUS_TONE_COLORS = {
  success: 'green',
  active: 'green',
  paid: 'green',
  processed: 'green',
  warning: 'gold',
  pending: 'gold',
  no_op: 'gold',
  duplicate: 'gold',
  error: 'red',
  failed: 'red',
  financial_failure: 'red',
  default: 'default',
  unknown: 'default',
  missing: 'default',
};

const getTagFromValue = (value, map = {}) => {
  const normalized = normalizeStringValue(value).toLowerCase();
  const color = map[normalized] || STATUS_TONE_COLORS[normalized] || 'default';
  return <Tag color={color}>{normalizeStringValue(value) || '-'}</Tag>;
};

const STATUS_COLOR = {
  webhook_status: {
    unknown: 'default',
    received: 'processing',
  },
  activation_outcome: {
    activated: 'green',
    no_change: 'default',
    noop: 'gold',
  },
  current_subscription_status: {
    active: 'green',
    trialing: 'processing',
    past_due: 'gold',
    canceled: 'default',
    blocked: 'red',
  },
  payment_state: {
    paid: 'green',
    confirmed: 'green',
    pending: 'processing',
    failed: 'red',
    overdue: 'gold',
    canceled: 'default',
  },
  timeline_category: {
    webhook: 'purple',
    runtime: 'cyan',
    transition: 'gold',
    operational: 'blue',
    payment_intent: 'geekblue',
    charge: 'orange',
    invoice: 'green',
  },
  timeline_severity: {
    info: 'blue',
    warning: 'gold',
    error: 'red',
    critical: 'magenta',
  },
  webhook_processing_status: {
    received: 'processing',
    ignored: 'gold',
  },
  webhook_provider: {
    asaas: 'blue',
    stripe: 'purple',
    mock: 'default',
  },
};

const isEmptyValue = (value) => {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return normalizeStringValue(value) === '';
  }
  return false;
};

const buildResolveParams = (values) => {
  const params = {
    payment_intent_id: normalizeStringValue(values.payment_intent_id),
    request_id: normalizeStringValue(values.request_id),
    billing_correlation_id: normalizeStringValue(values.billing_correlation_id),
    provider_charge_ref: normalizeStringValue(values.provider_charge_ref),
    external_charge_ref: normalizeStringValue(values.external_charge_ref),
    charge_id: normalizeStringValue(values.charge_id),
    invoice_id: normalizeStringValue(values.invoice_id),
    correlation_id: normalizeStringValue(values.correlation_id),
    tenant_id: normalizeStringValue(values.tenant_id),
    from_at: values.from ? values.from.toISOString() : undefined,
    to_at: values.to ? values.to.toISOString() : undefined,
  };

  return Object.keys(params).reduce((acc, key) => {
    if (!isEmptyValue(params[key])) {
      acc[key] = params[key];
    }
    return acc;
  }, {});
};

const buildTimelineParams = (values) => {
  const categories = Array.isArray(values.categories)
    ? values.categories
      .map((entry) => normalizeStringValue(entry))
      .filter((entry) => TIMELINE_ALLOWED_CATEGORIES.has(entry))
    : [];

  const params = {
    payment_intent_id: normalizeStringValue(values.payment_intent_id),
    request_id: normalizeStringValue(values.request_id),
    billing_correlation_id: normalizeStringValue(values.billing_correlation_id),
    provider_charge_ref: normalizeStringValue(values.provider_charge_ref),
    external_charge_ref: normalizeStringValue(values.external_charge_ref),
    charge_id: normalizeStringValue(values.charge_id),
    invoice_id: normalizeStringValue(values.invoice_id),
    correlation_id: normalizeStringValue(values.correlation_id),
    tenant_id: normalizeStringValue(values.tenant_id),
    from: values.from ? values.from.toISOString() : undefined,
    to: values.to ? values.to.toISOString() : undefined,
    categories: categories.length > 0 ? categories : undefined,
    include_payload: values.include_payload === true,
  };

  return Object.keys(params).reduce((acc, key) => {
    if (!isEmptyValue(params[key])) {
      acc[key] = params[key];
    }
    return acc;
  }, {});
};

const buildWebhookParams = (values) => {
  const parsedLimit = Number.parseInt(String(values.limit ?? ''), 10);
  const parsedOffset = Number.parseInt(String(values.offset ?? ''), 10);

  const limit = Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : 50;
  const offset = Number.isFinite(parsedOffset) && parsedOffset >= 0 ? parsedOffset : 0;

  const normalizedProcessingStatus = normalizeStringValue(values.processing_status);

  const params = {
    provider_key: normalizeStringValue(values.provider_key),
    provider_event_id: normalizeStringValue(values.provider_event_id),
    provider_charge_ref: normalizeStringValue(values.provider_charge_ref),
    event_type: normalizeStringValue(values.event_type),
    processing_status: WEBHOOK_ALLOWED_PROCESSING_STATUS.has(normalizedProcessingStatus)
      ? normalizedProcessingStatus
      : undefined,
    from: values.from ? values.from.toISOString() : undefined,
    to: values.to ? values.to.toISOString() : undefined,
    limit,
    offset,
  };

  return Object.keys(params).reduce((acc, key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
};

const buildCandidatesColumns = () => [
  {
    title: 'matched_by',
    dataIndex: 'matched_by',
    key: 'matched_by',
    width: 150,
    ellipsis: true,
    render: (value) => (isEmptyValue(value) ? '-' : value),
  },
  {
    title: 'tenant_id',
    dataIndex: 'tenant_id',
    key: 'tenant_id',
    width: 180,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
  {
    title: 'subscription_id',
    dataIndex: 'subscription_id',
    key: 'subscription_id',
    width: 180,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
  {
    title: 'invoice_id',
    dataIndex: 'invoice_id',
    key: 'invoice_id',
    width: 190,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
  {
    title: 'charge_id',
    dataIndex: 'charge_id',
    key: 'charge_id',
    width: 190,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
  {
    title: 'payment_intent_id',
    dataIndex: 'payment_intent_id',
    key: 'payment_intent_id',
    width: 220,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
  {
    title: 'request_id',
    dataIndex: 'request_id',
    key: 'request_id',
    width: 220,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
  {
    title: 'correlation_id',
    dataIndex: 'correlation_id',
    key: 'correlation_id',
    width: 220,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
  {
    title: 'billing_correlation_id',
    dataIndex: 'billing_correlation_id',
    key: 'billing_correlation_id',
    width: 240,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
  {
    title: 'provider_key',
    dataIndex: 'provider_key',
    key: 'provider_key',
    width: 140,
    ellipsis: true,
    render: (value) => (isEmptyValue(value) ? '-' : value),
  },
  {
    title: 'provider_charge_ref',
    dataIndex: 'provider_charge_ref',
    key: 'provider_charge_ref',
    width: 220,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
  {
    title: 'external_charge_ref',
    dataIndex: 'external_charge_ref',
    key: 'external_charge_ref',
    width: 220,
    ellipsis: true,
    render: (value) => renderCopyableValue(value),
  },
];

const renderRefsSummary = (refs = {}) => {
  const safeRefs = refs && typeof refs === 'object' ? refs : {};
  const items = TIMELINE_REFS_FIELDS
    .map((field) => ({ key: field, value: safeRefs[field] }))
    .filter((entry) => !isEmptyValue(entry.value));

  if (items.length === 0) {
    return '-';
  }

  return (
    <Space wrap size={[6, 6]}>
      {items.map((entry) => (
        <Tag key={entry.key}>{`${entry.key}: ${String(entry.value)}`}</Tag>
      ))}
    </Space>
  );
};

const renderSanitizedPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return '-';
  }

  const items = PAYLOAD_SANITIZED_FIELDS
    .map((field) => ({ key: field, value: payload[field] }))
    .filter((entry) => !isEmptyValue(entry.value));

  if (items.length === 0) {
    return <Text type="secondary">payload_sanitized disponivel sem campos canônicos mapeados</Text>;
  }

  return (
    <Space wrap size={[6, 6]}>
      {items.map((entry) => (
        <Tag key={entry.key}>{`${entry.key}: ${String(entry.value)}`}</Tag>
      ))}
    </Space>
  );
};

const ResolveCaseSummary = ({ caseData }) => {
  const renderValue = (field, value) => {
    if (field === 'financial_failure') {
      return value === true ? <Tag color="red">true</Tag> : <Tag color="green">false</Tag>;
    }
    if (field === 'replay_or_noop') {
      return value === true ? <Tag color="gold">true</Tag> : <Tag color="blue">false</Tag>;
    }
    if (field === 'webhook_status') {
      return getTagFromValue(value, STATUS_COLOR.webhook_status);
    }
    if (field === 'activation_outcome') {
      return getTagFromValue(value, STATUS_COLOR.activation_outcome);
    }
    if (field === 'current_subscription_status') {
      return getTagFromValue(value, STATUS_COLOR.current_subscription_status);
    }
    if (field === 'payment_state') {
      return getTagFromValue(value, STATUS_COLOR.payment_state);
    }
    if (field === 'first_seen_at' || field === 'last_seen_at') {
      return formatDateTime(value);
    }
    return isEmptyValue(value) ? '-' : String(value);
  };

  return (
    <Card size="small" title="Resumo do Caso">
      <Descriptions bordered size="small" column={1}>
        {RESOLVE_CANONICAL_FIELDS.map((field) => (
          <Descriptions.Item key={field.key} label={field.label}>
            {renderValue(field.key, caseData?.[field.key])}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </Card>
  );
};

const ResolveCaseTab = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [validationError, setValidationError] = React.useState('');
  const [responseData, setResponseData] = React.useState(null);
  const [hasSearched, setHasSearched] = React.useState(false);

  const validateResolveInputs = (values) => {
    const keyValues = CASE_KEY_FIELDS.map((field) => normalizeStringValue(values[field]));
    const hasAtLeastOneKey = keyValues.some((value) => value.length > 0);

    if (!hasAtLeastOneKey) {
      setValidationError('Preencha pelo menos uma chave de busca para resolver o caso.');
      return false;
    }

    const tenantOnly =
      normalizeStringValue(values.tenant_id).length > 0
      && CASE_KEY_FIELDS
        .filter((field) => field !== 'tenant_id')
        .every((field) => normalizeStringValue(values[field]).length === 0);

    if (tenantOnly && (!values.from || !values.to)) {
      setValidationError('Quando usar apenas tenant_id, informe obrigatoriamente os campos from e to.');
      form.setFields([
        {
          name: 'from',
          errors: !values.from ? ['Informe from quando usar apenas tenant_id.'] : [],
        },
        {
          name: 'to',
          errors: !values.to ? ['Informe to quando usar apenas tenant_id.'] : [],
        },
      ]);
      return false;
    }

    setValidationError('');
    return true;
  };

  const executeResolve = async (params) => {
    setLoading(true);
    setError(null);
    setResponseData(null);
    setValidationError('');
    setHasSearched(true);

    try {
      const data = await platformSaasBillingOperationsService.resolvePaymentCase(params);
      setResponseData(data || null);
    } catch (requestError) {
      setError(
        buildOperationalErrorMessage(
          requestError,
          'Nao foi possivel resolver o caso de billing no momento.'
        )
      );
      setResponseData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    const values = form.getFieldsValue();
    if (!validateResolveInputs(values)) {
      return;
    }

    const params = buildResolveParams(values);
    await executeResolve(params);
  };

  const handleClear = () => {
    form.resetFields();
    setLoading(false);
    setError(null);
    setValidationError('');
    setResponseData(null);
    setHasSearched(false);
  };

  const candidateRows = Array.isArray(responseData?.candidates) ? responseData.candidates : [];
  const isAmbiguity = responseData?.ambiguity === true;
  const isMatched = responseData?.matched === true && Boolean(responseData?.case);
  const isNoMatch = hasSearched && !loading && !error && !isAmbiguity && !isMatched;

  const candidateColumns = [
    ...buildCandidatesColumns(),
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small" title="Resolver Caso">
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            Identifique um caso unico de cobranca por chaves operacionais para analise somente leitura.
          </Text>
          <Space wrap>
            <Tag color="blue">FE-2</Tag>
            <Tag>Read-only</Tag>
            <Tag>Sem payload</Tag>
          </Space>
        </Space>
      </Card>

      {validationError ? (
        <Alert
          type="warning"
          showIcon
          message="Validacao da busca"
          description={validationError}
        />
      ) : null}

      <Card size="small" title="Filtros de Busca">
        <Form layout="vertical" form={form}>
          <Row gutter={[12, 0]}>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="payment_intent_id" name="payment_intent_id">
                <Input placeholder="payment_intent_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="request_id" name="request_id">
                <Input placeholder="request_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="billing_correlation_id" name="billing_correlation_id">
                <Input placeholder="billing_correlation_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="provider_charge_ref" name="provider_charge_ref">
                <Input placeholder="provider_charge_ref" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="external_charge_ref" name="external_charge_ref">
                <Input placeholder="external_charge_ref" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="charge_id" name="charge_id">
                <Input placeholder="charge_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="invoice_id" name="invoice_id">
                <Input placeholder="invoice_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="correlation_id" name="correlation_id">
                <Input placeholder="correlation_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="tenant_id" name="tenant_id">
                <Input placeholder="tenant_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="from" name="from">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="to" name="to">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Space wrap>
            <Button type="primary" onClick={handleSearch} loading={loading}>
              Buscar
            </Button>
            <Button onClick={handleClear}>Limpar filtros</Button>
          </Space>
        </Form>
      </Card>

      <Card size="small" title="Resultado da Resolucao">
        <Text type="secondary">Estados: inicial, sem match, ambiguidade, sucesso e erro.</Text>
        {loading ? <RuntimeLoadingState tip="Resolvendo caso de billing..." /> : null}

        {!loading && error ? (
          <RuntimeErrorState
            title="Erro ao resolver caso"
            description={error}
          />
        ) : null}

        {!loading && !error && isNoMatch ? (
          <RuntimeEmptyState description="Nenhum caso encontrado." />
        ) : null}

        {!loading && !error && isAmbiguity ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="Multiplos candidatos encontrados"
              description="A resolucao retornou mais de um candidato. Revise os registros e refine os filtros. Nenhum candidato foi selecionado automaticamente."
            />

            <Table
              rowKey={(row, index) => `${String(row?.matched_by || '-')}-${index}`}
              columns={candidateColumns}
              dataSource={candidateRows}
              pagination={false}
              size="small"
              scroll={{ x: 1400 }}
            />
          </Space>
        ) : null}

        {!loading && !error && isMatched ? <ResolveCaseSummary caseData={responseData.case} /> : null}

        {!loading && !error && !hasSearched ? (
          <RuntimeEmptyState description="Preencha filtros e clique em Buscar." />
        ) : null}
      </Card>
    </Space>
  );
};

const TimelineTab = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [validationError, setValidationError] = React.useState('');
  const [responseData, setResponseData] = React.useState(null);
  const [hasSearched, setHasSearched] = React.useState(false);

  const includePayload = Form.useWatch('include_payload', form) === true;

  const validateTimelineInputs = (values) => {
    const keyValues = CASE_KEY_FIELDS.map((field) => normalizeStringValue(values[field]));
    const hasAtLeastOneKey = keyValues.some((value) => value.length > 0);

    if (!hasAtLeastOneKey) {
      setValidationError('Preencha pelo menos uma chave de busca para consultar a timeline.');
      return false;
    }

    const tenantOnly =
      normalizeStringValue(values.tenant_id).length > 0
      && CASE_KEY_FIELDS
        .filter((field) => field !== 'tenant_id')
        .every((field) => normalizeStringValue(values[field]).length === 0);

    if (tenantOnly && (!values.from || !values.to)) {
      setValidationError('Quando usar apenas tenant_id, informe obrigatoriamente os campos from e to.');
      form.setFields([
        {
          name: 'from',
          errors: !values.from ? ['Informe from quando usar apenas tenant_id.'] : [],
        },
        {
          name: 'to',
          errors: !values.to ? ['Informe to quando usar apenas tenant_id.'] : [],
        },
      ]);
      return false;
    }

    setValidationError('');
    return true;
  };

  const executeTimeline = async (params) => {
    setLoading(true);
    setError(null);
    setResponseData(null);
    setValidationError('');
    setHasSearched(true);

    try {
      const data = await platformSaasBillingOperationsService.getPaymentCaseTimeline(params);
      setResponseData(data || null);
    } catch (requestError) {
      setError(
        buildOperationalErrorMessage(
          requestError,
          'Nao foi possivel consultar a timeline do caso no momento.'
        )
      );
      setResponseData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    const values = form.getFieldsValue();
    if (!validateTimelineInputs(values)) {
      return;
    }

    const params = buildTimelineParams(values);
    await executeTimeline(params);
  };

  const handleClear = () => {
    form.resetFields();
    form.setFieldValue('include_payload', false);
    setLoading(false);
    setError(null);
    setValidationError('');
    setResponseData(null);
    setHasSearched(false);
  };

  const candidateRows = Array.isArray(responseData?.candidates) ? responseData.candidates : [];
  const timelineRows = Array.isArray(responseData?.timeline) ? responseData.timeline : [];

  const isAmbiguity = responseData?.ambiguity === true;
  const isMatched = responseData?.matched === true;
  const isNoMatch = hasSearched && !loading && !error && !isAmbiguity && !isMatched;
  const isMatchedWithEmptyTimeline = isMatched && timelineRows.length === 0;
  const isMatchedWithTimeline = isMatched && timelineRows.length > 0;

  const timelineColumns = [
    {
      title: 'occurred_at',
      dataIndex: 'occurred_at',
      key: 'occurred_at',
      render: (value) => formatDateCell(value),
      width: 210,
      sorter: (a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'category',
      dataIndex: 'category',
      key: 'category',
      width: 140,
      render: (value) => getTagFromValue(value, STATUS_COLOR.timeline_category),
    },
    {
      title: 'event_type',
      dataIndex: 'event_type',
      key: 'event_type',
      width: 180,
      ellipsis: true,
      render: (value) => <Tag>{isEmptyValue(value) ? '-' : value}</Tag>,
    },
    {
      title: 'severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 120,
      render: (value) => getTagFromValue(value, STATUS_COLOR.timeline_severity),
    },
    {
      title: 'source',
      dataIndex: 'source',
      key: 'source',
      width: 140,
      ellipsis: true,
      render: (value) => (isEmptyValue(value) ? '-' : value),
    },
    {
      title: 'message',
      dataIndex: 'message',
      key: 'message',
      render: (value) => (isEmptyValue(value) ? '-' : value),
      width: 280,
      ellipsis: true,
    },
    {
      title: 'reason_code',
      dataIndex: 'reason_code',
      key: 'reason_code',
      width: 160,
      ellipsis: true,
      render: (value) => renderCopyableValue(value, { maxWidth: 160, copyable: false }),
    },
    {
      title: 'failure_reason',
      dataIndex: 'failure_reason',
      key: 'failure_reason',
      width: 220,
      ellipsis: true,
      render: (value) => renderCopyableValue(value, { maxWidth: 220, copyable: false }),
    },
    {
      title: 'validation_reason',
      dataIndex: 'validation_reason',
      key: 'validation_reason',
      width: 220,
      ellipsis: true,
      render: (value) => renderCopyableValue(value, { maxWidth: 220, copyable: false }),
    },
    {
      title: 'noop_reason',
      dataIndex: 'noop_reason',
      key: 'noop_reason',
      width: 220,
      ellipsis: true,
      render: (value) => renderCopyableValue(value, { maxWidth: 220, copyable: false }),
    },
    {
      title: 'refs',
      dataIndex: 'refs',
      key: 'refs',
      render: (value) => renderRefsSummary(value),
      width: 360,
    },
  ];

  if (includePayload) {
    timelineColumns.push({
      title: 'payload_sanitized',
      dataIndex: 'payload_sanitized',
      key: 'payload_sanitized',
      render: (value) => renderSanitizedPayload(value),
      width: 360,
    });
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small" title="Timeline">
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            Visualize a trilha cronologica do caso resolvido, com categorias e severidade em modo read-only.
          </Text>
          <Space wrap>
            <Tag color="blue">FE-3</Tag>
            <Tag>Read-only</Tag>
            <Tag>Sem acoes mutaveis</Tag>
          </Space>
        </Space>
      </Card>

      {validationError ? (
        <Alert
          type="warning"
          showIcon
          message="Validacao da busca"
          description={validationError}
        />
      ) : null}

      {includePayload ? (
        <Alert
          type="warning"
          showIcon
          message="include_payload ativo"
          description="Somente payload_sanitized sera exibido. Nenhum payload bruto ou campo desconhecido sera renderizado."
        />
      ) : null}

      <Card size="small" title="Filtros da Timeline">
        <Form layout="vertical" form={form} initialValues={{ include_payload: false }}>
          <Row gutter={[12, 0]}>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="payment_intent_id" name="payment_intent_id">
                <Input placeholder="payment_intent_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="request_id" name="request_id">
                <Input placeholder="request_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="billing_correlation_id" name="billing_correlation_id">
                <Input placeholder="billing_correlation_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="provider_charge_ref" name="provider_charge_ref">
                <Input placeholder="provider_charge_ref" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="external_charge_ref" name="external_charge_ref">
                <Input placeholder="external_charge_ref" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="charge_id" name="charge_id">
                <Input placeholder="charge_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="invoice_id" name="invoice_id">
                <Input placeholder="invoice_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="correlation_id" name="correlation_id">
                <Input placeholder="correlation_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="tenant_id" name="tenant_id">
                <Input placeholder="tenant_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="from" name="from">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="to" name="to">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={16}>
              <Form.Item label="categories" name="categories">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Selecione categorias"
                  options={TIMELINE_CATEGORY_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item label="include_payload" name="include_payload" valuePropName="checked">
                <Switch checkedChildren="ON" unCheckedChildren="OFF" />
              </Form.Item>
            </Col>
          </Row>

          <Space wrap>
            <Button type="primary" onClick={handleSearch} loading={loading}>
              Buscar
            </Button>
            <Button onClick={handleClear}>Limpar filtros</Button>
          </Space>
        </Form>
      </Card>

      <Card size="small" title="Resultado da Timeline">
        <Text type="secondary">Estados: inicial, sem match, ambiguidade, sucesso e erro.</Text>
        {loading ? <RuntimeLoadingState tip="Consultando timeline do caso..." /> : null}

        {!loading && error ? (
          <RuntimeErrorState
            title="Erro ao consultar timeline"
            description={error}
          />
        ) : null}

        {!loading && !error && isNoMatch ? (
          <RuntimeEmptyState description="Nenhum caso encontrado." />
        ) : null}

        {!loading && !error && isAmbiguity ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="Multiplos candidatos encontrados"
              description="A consulta retornou mais de um candidato. Refine os filtros para identificar um caso unico. Nenhum candidato foi selecionado automaticamente."
            />

            <Table
              rowKey={(row, index) => `${String(row?.matched_by || '-')}-${index}`}
              columns={buildCandidatesColumns()}
              dataSource={candidateRows}
              pagination={false}
              size="small"
              scroll={{ x: 1400 }}
            />
          </Space>
        ) : null}

        {!loading && !error && isMatchedWithEmptyTimeline ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {responseData?.case ? <ResolveCaseSummary caseData={responseData.case} /> : null}
            <RuntimeEmptyState description="Caso resolvido com sucesso, mas sem eventos na timeline para o filtro informado." />
          </Space>
        ) : null}

        {!loading && !error && isMatchedWithTimeline ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {responseData?.case ? <ResolveCaseSummary caseData={responseData.case} /> : null}

            <Table
              rowKey={(row, index) => `${String(row?.occurred_at || 'na')}-${String(row?.event_type || 'na')}-${index}`}
              columns={timelineColumns}
              dataSource={timelineRows}
              pagination={false}
              size="small"
              scroll={{ x: 2200 }}
            />
          </Space>
        ) : null}

        {!loading && !error && !hasSearched ? (
          <RuntimeEmptyState description="Preencha filtros e clique em Buscar." />
        ) : null}
      </Card>
    </Space>
  );
};

const WebhooksTab = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [validationError, setValidationError] = React.useState('');
  const [responseData, setResponseData] = React.useState(null);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [appliedParams, setAppliedParams] = React.useState({ limit: 50, offset: 0 });

  const executeWebhooks = async (params) => {
    setLoading(true);
    setError(null);
    setValidationError('');
    setHasSearched(true);

    try {
      const data = await platformSaasBillingOperationsService.listWebhookEvents(params);
      setResponseData(data || null);
      setAppliedParams(params);
    } catch (requestError) {
      setError(
        buildOperationalErrorMessage(
          requestError,
          'Nao foi possivel consultar os eventos de webhook no momento.'
        )
      );
      setResponseData(null);
    } finally {
      setLoading(false);
    }
  };

  const validateWebhookInputs = (values) => {
    const parsedLimit = Number.parseInt(String(values.limit ?? ''), 10);
    const parsedOffset = Number.parseInt(String(values.offset ?? ''), 10);

    if (!Number.isFinite(parsedLimit) || parsedLimit <= 0) {
      setValidationError('O campo limit deve ser um numero inteiro maior que zero.');
      form.setFields([{ name: 'limit', errors: ['Informe um limit valido (> 0).'] }]);
      return false;
    }

    if (!Number.isFinite(parsedOffset) || parsedOffset < 0) {
      setValidationError('O campo offset deve ser um numero inteiro maior ou igual a zero.');
      form.setFields([{ name: 'offset', errors: ['Informe um offset valido (>= 0).'] }]);
      return false;
    }

    const normalizedProcessingStatus = normalizeStringValue(values.processing_status);
    if (
      normalizedProcessingStatus
      && !WEBHOOK_ALLOWED_PROCESSING_STATUS.has(normalizedProcessingStatus)
    ) {
      setValidationError('O campo processing_status deve usar apenas valores permitidos.');
      form.setFields([
        {
          name: 'processing_status',
          errors: ['Use: received, processed, duplicate, failed, ignored ou unknown.'],
        },
      ]);
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleSearch = async () => {
    const values = form.getFieldsValue();
    if (!validateWebhookInputs(values)) {
      return;
    }
    const params = buildWebhookParams(values);
    await executeWebhooks(params);
  };

  const handleClear = () => {
    form.resetFields();
    form.setFieldsValue({ limit: 50, offset: 0 });
    setLoading(false);
    setError(null);
    setValidationError('');
    setResponseData(null);
    setHasSearched(false);
    setAppliedParams({ limit: 50, offset: 0 });
  };

  const goToOffset = async (nextOffset) => {
    const safeOffset = Math.max(0, Number(nextOffset) || 0);
    const params = {
      ...appliedParams,
      offset: safeOffset,
    };
    form.setFieldValue('offset', safeOffset);
    await executeWebhooks(params);
  };

  const rows = Array.isArray(responseData?.items) ? responseData.items : [];
  const currentLimit = Number(appliedParams?.limit || 50);
  const currentOffset = Number(appliedParams?.offset || 0);
  const returnedCount = Number(responseData?.returned_count || rows.length || 0);

  const hasData = !loading && !error && rows.length > 0;
  const isEmpty = hasSearched && !loading && !error && rows.length === 0;
  const canGoPrev = currentOffset > 0 && !loading;
  const canGoNext = returnedCount >= currentLimit && currentLimit > 0 && !loading;

  const webhookColumns = [
    {
      title: 'provider_key',
      dataIndex: 'provider_key',
      key: 'provider_key',
      width: 130,
      render: (value) => getTagFromValue(value, STATUS_COLOR.webhook_provider),
    },
    {
      title: 'provider_event_id',
      dataIndex: 'provider_event_id',
      key: 'provider_event_id',
      width: 220,
      ellipsis: true,
      render: (value) => renderCopyableValue(value),
    },
    {
      title: 'provider_charge_ref',
      dataIndex: 'provider_charge_ref',
      key: 'provider_charge_ref',
      width: 220,
      ellipsis: true,
      render: (value) => renderCopyableValue(value),
    },
    {
      title: 'event_type',
      dataIndex: 'event_type',
      key: 'event_type',
      width: 200,
      ellipsis: true,
      render: (value) => <Tag>{isEmptyValue(value) ? '-' : value}</Tag>,
    },
    {
      title: 'processing_status',
      dataIndex: 'processing_status',
      key: 'processing_status',
      width: 150,
      render: (value) => getTagFromValue(value, STATUS_COLOR.webhook_processing_status),
    },
    {
      title: 'process_attempts',
      dataIndex: 'process_attempts',
      key: 'process_attempts',
      width: 130,
      render: (value) => (Number.isFinite(Number(value)) ? Number(value) : '-'),
    },
    {
      title: 'first_seen_at',
      dataIndex: 'first_seen_at',
      key: 'first_seen_at',
      width: 210,
      render: (value) => formatDateCell(value),
    },
    {
      title: 'last_seen_at',
      dataIndex: 'last_seen_at',
      key: 'last_seen_at',
      width: 210,
      render: (value) => formatDateCell(value),
    },
    {
      title: 'processed_at',
      dataIndex: 'processed_at',
      key: 'processed_at',
      width: 210,
      render: (value) => formatDateCell(value),
    },
    {
      title: 'payload_hash',
      dataIndex: 'payload_hash',
      key: 'payload_hash',
      width: 220,
      ellipsis: true,
      render: (value) => renderCopyableValue(value),
    },
    {
      title: 'last_error_sanitized',
      dataIndex: 'last_error_sanitized',
      key: 'last_error_sanitized',
      ellipsis: true,
      render: (value) => renderCopyableValue(value, { maxWidth: 260, copyable: false }),
      width: 260,
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small" title="Webhooks">
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            Consulte eventos de webhook em modo read-only para investigacao operacional e rastreabilidade.
          </Text>
          <Space wrap>
            <Tag color="blue">FE-4</Tag>
            <Tag>Read-only</Tag>
            <Tag>Sem payload bruto</Tag>
          </Space>
        </Space>
      </Card>

      {validationError ? (
        <Alert
          type="warning"
          showIcon
          message="Validacao dos filtros"
          description={validationError}
        />
      ) : null}

      <Card size="small" title="Filtros de Webhooks">
        <Form layout="vertical" form={form} initialValues={{ limit: 50, offset: 0 }}>
          <Row gutter={[12, 0]}>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="provider_key" name="provider_key">
                <Input placeholder="provider_key" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="provider_event_id" name="provider_event_id">
                <Input placeholder="provider_event_id" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="provider_charge_ref" name="provider_charge_ref">
                <Input placeholder="provider_charge_ref" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="event_type" name="event_type">
                <Input placeholder="event_type" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="processing_status" name="processing_status">
                <Select
                  allowClear
                  placeholder="processing_status"
                  options={WEBHOOK_PROCESSING_STATUS_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="from" name="from">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="to" name="to">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={4}>
              <Form.Item label="limit" name="limit">
                <Input type="number" min={1} step={1} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={4}>
              <Form.Item label="offset" name="offset">
                <Input type="number" min={0} step={1} />
              </Form.Item>
            </Col>
          </Row>

          <Space wrap>
            <Button type="primary" onClick={handleSearch} loading={loading}>
              Buscar
            </Button>
            <Button onClick={handleClear}>Limpar filtros</Button>
          </Space>
        </Form>
      </Card>

      <Card size="small" title="Resultado dos Webhooks">
        <Text type="secondary">Estados: inicial, sem resultado, sucesso e erro.</Text>
        {loading ? <RuntimeLoadingState tip="Consultando eventos de webhook..." /> : null}

        {!loading && error ? (
          <RuntimeErrorState
            title="Erro ao consultar webhooks"
            description={error}
          />
        ) : null}

        {!loading && !error && isEmpty ? (
          <RuntimeEmptyState description="Nenhum evento encontrado." />
        ) : null}

        {!loading && !error && hasData ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Table
              rowKey={(row, index) => `${String(row?.provider_event_id || 'na')}-${index}`}
              columns={webhookColumns}
              dataSource={rows}
              pagination={false}
              size="small"
              scroll={{ x: 1900 }}
            />
          </Space>
        ) : null}

        {!loading && !error && hasSearched ? (
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <Text type="secondary">
              {`offset atual=${currentOffset} | limit=${currentLimit} | quantidade retornada=${returnedCount}`}
            </Text>

            <Space>
              <Button
                onClick={() => goToOffset(Math.max(0, currentOffset - currentLimit))}
                disabled={!canGoPrev}
              >
                Anterior
              </Button>
              <Button
                onClick={() => goToOffset(currentOffset + currentLimit)}
                disabled={!canGoNext}
              >
                Proxima
              </Button>
            </Space>
          </Space>
        ) : null}

        {!loading && !error && !hasSearched ? (
          <RuntimeEmptyState description="Preencha filtros e clique em Buscar." />
        ) : null}
      </Card>
    </Space>
  );
};

const PlaceholderTabCard = ({ title, description, statusLabel, emptyDescription }) => {
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small" title={title}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary">{description}</Text>
          <Space wrap>
            <Tag color="blue">FE-1</Tag>
            <Tag>{statusLabel}</Tag>
            <Tag>Platform Admin</Tag>
          </Space>
        </Space>
      </Card>

      <Card size="small">
        <RuntimeEmptyState description={emptyDescription} />
      </Card>
    </Space>
  );
};

const PlatformBillingOperationsPage = () => {
  const [activeKey, setActiveKey] = React.useState('resolver-caso');
  const [statusMode] = React.useState('ready');

  const tabItems = [
    {
      key: 'resolver-caso',
      label: 'Resolver Caso',
      children: <ResolveCaseTab />,
    },
    {
      key: 'timeline',
      label: 'Timeline',
      children: <TimelineTab />,
    },
    {
      key: 'webhooks',
      label: 'Webhooks',
      children: <WebhooksTab />,
    },
    ...PLACEHOLDER_TAB_ITEMS.map((tab) => ({
      key: tab.key,
      label: tab.label,
      children: (
        <PlaceholderTabCard
          title={tab.title}
          description={tab.description}
          statusLabel={tab.statusLabel}
          emptyDescription={tab.emptyDescription}
        />
      ),
    })),
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>
        Billing Operations
      </Title>
      <Text type="secondary">
        Area operacional inicial para platform admin investigar billing SaaS em modo estritamente read-only.
      </Text>

      <Card size="small" style={{ marginTop: 16 }}>
        <Space wrap>
          <Tag color="green">Protected Route</Tag>
          <Tag>Platform Admin Only</Tag>
          <Tag>Sem acoes mutaveis</Tag>
        </Space>
      </Card>

      <Card size="small" style={{ marginTop: 16 }}>
        {statusMode === 'loading' ? <RuntimeLoadingState tip="Carregando Billing Operations..." /> : null}
        {statusMode === 'error' ? (
          <RuntimeErrorState
            title="Falha ao carregar Billing Operations"
            description="Nao foi possivel carregar a estrutura inicial da area operacional."
          />
        ) : null}
        {statusMode === 'ready' ? (
          <Tabs
            activeKey={activeKey}
            onChange={setActiveKey}
            items={tabItems}
            destroyInactiveTabPane={false}
          />
        ) : null}
      </Card>
    </div>
  );
};

export default PlatformBillingOperationsPage;